import { displayJourneyUsingMetatags } from '@eventbrite/branchio-utils';
import { sdkRequest } from '@eventbrite/http';
import { setLanguage } from '@eventbrite/i18n';
import {
    addDimensions,
    trackAnalyticsEvent,
    trackInitialPageView,
} from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import React, { useEffect, useState } from 'react';
import BookmarkRoot from './components/Bookmark/BookmarkRoot';
import CreatorCollectionsRoot from './components/CreatorCollections/CreatorCollectionsRoot';
import { EnhancedListingComponents } from './components/EnhancedListingComponents/EnhancedListingComponents';
import EventMapRoot from './components/EventMap/EventMapRoot';
import { ExpiredEvents } from './components/ExpiredEvents/ExpiredEvents';
import { FollowOrganizerController } from './components/FollowOrganizer/FollowOrganizerController';
import { NotificationBar } from './components/NotificationBar/NotificationBar';
import { ReportThisEventRoot } from './components/ReportThisEvent/ReportThisEventRoot';
import SeriesDatesDropdownRoot from './components/SeriesDatesDropdown/SeriesDatesDropdownRoot';
import ShareEventRoot from './components/ShareEvent/ShareEventRoot';
import { Analytics } from './components/utils/analytics';
import { useLocalizedDates } from './components/utils/localizedDates';
import { STATSIG_EVENT_NAMES } from './constants';
import {
    OrganizerContext,
    SettingsContext,
    TrackingContext,
    UserContext,
} from './context';
import { NotificationsContextProvider } from './contexts';
import { getTrackingContextFrom } from './getTrackingContext';
import type {
    AppProps,
    CollectionResponse,
    EventCollectionsResponse,
} from './types';
import { GACodes } from './types';

const setUpDimensions = (props: AppProps): void => {
    const {
        app_name: appName,
        app_version: appVersion,
        event,
        gaSettings,
    } = props;
    const {
        activeScopeUserId,
        correlationId,
        guestPartnerId,
        userPartnerId: userId,
        guestPartnerId: guestId,
    } = gaSettings;
    const {
        id: eventId,
        ownerId: eventOwnerUserId,
        source: eventSource,
    } = event;

    const dimensions = {
        activeScopeUserId,
        appName,
        appVersion,
        correlationId,
        eventId,
        eventOwnerUserId,
        eventSource,
        experimentId: 'OldListing',
        guestId,
        guestPartnerId,
        userId,
    };

    addDimensions(dimensions);
};

const useCreatorCollections = (
    eventId: string,
    launchLinkToCreatorCollectionsOnListing: boolean,
    isExpiredEvent: boolean,
) => {
    const [collections, setCollections] = useState<
        CollectionResponse[] | undefined
    >(undefined);

    useEffect(() => {
        let isMounted = true;

        displayJourneyUsingMetatags({
            name: 'DisplayBranchBanner',
            content: '1',
            extraData: {
                customActionParams: {
                    EventId: eventId,
                },
            },
        });

        if (isExpiredEvent) {
            return;
        }

        if (launchLinkToCreatorCollectionsOnListing) {
            sdkRequest(`/events/${eventId}/collections/public/`).then(
                (storedCollections: EventCollectionsResponse) => {
                    if (storedCollections && isMounted)
                        return setCollections(storedCollections.collections);
                },
            );
        } else {
            return setCollections([]);
        }
        return () => {
            isMounted = false;
        };
    }, [eventId, launchLinkToCreatorCollectionsOnListing, isExpiredEvent]);

    return collections;
};

export const App: React.FC<AppProps> = (props: AppProps) => {
    const {
        env: {
            localeInfo: { locale },
        },
        user: { isAuthenticated, id: userId },
        event: {
            childEvents,
            end,
            hideEndDate,
            hideStartDate,
            id: eventId,
            isOnlineEvent,
            isSeriesParent,
            name: eventName,
            start,
            url: eventUrl,
            isExpiredEvent = false,
        },
        settings: {
            featureFlags: {
                launchLinkToCreatorCollectionsOnListing = false,
                disable_require_base_config_js_on_listings = false,
            } = {},
        },
        gaSettings,
        tracking,
        components: {
            remindMe: { isRemindedEvent = false },
            bookmark: { isBookmarkedEvent = false } = {},
            followOrganizer: { isGDPRCountry },
            contactOrganizer: { captchaKey },
        },
        organizer: {
            id: organizerId,
            name: organizerName,
            url: organizerUrl,
            profilePicture: organizerProfilePicture,
            description: organizerDescription,
            eventTitle: organizerEventTitle,
            orgFacebook: organizerFacebook,
            orgTwitter: organizerTwitter,
            orgWebsite: organizerWebsite,
        },
    } = props;

    setLanguage(locale);

    setUpDimensions(props);

    const modalContainer = React.useRef<HTMLDivElement | null>(null);
    const creatorCollections = useCreatorCollections(
        eventId,
        launchLinkToCreatorCollectionsOnListing,
        isExpiredEvent,
    );

    if (isExpiredEvent) {
        ExpiredEvents();
    }

    Analytics({ ...tracking, eventId });

    useLocalizedDates({
        end,
        hideEndDate,
        hideStartDate,
        isOnlineEvent,
        isSeriesParent,
        locale,
        start,
    });

    useEffect(() => {
        if (disable_require_base_config_js_on_listings) {
            trackInitialPageView(props.gaSettings);
        }
        trackAnalyticsEvent({
            category: GACodes.GA_CATEGORY_LISTING,
            action: GACodes.GA_ACTION_APP_LOAD,
            label: props.user.canManageEvent
                ? GACodes.GA_LABEL_CAN_EDIT
                : GACodes.GA_LABEL_CANNOT_EDIT,
        });
        logEvent(STATSIG_EVENT_NAMES.LISTING_APP_PAGE_VIEW);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The following two useEffects are for tracking purposes in Statsig,
    // we want to track when a user clicks in each of the follow and contact buttons.
    useEffect(() => {
        const followButtons = [
            {
                DOMnode: '.eds-btn--follow-mini',
                event: STATSIG_EVENT_NAMES.LISTING_ORGANIZER_FOLLOW_BUTTON,
            },
            {
                DOMnode: '.cc-list-actions .eds-btn--follow',
                event: STATSIG_EVENT_NAMES.LISTING_ORGANIZER_FOLLOW_BUTTON,
            },
        ];
        followButtons.map((el) => {
            document
                .querySelector(el.DOMnode)
                ?.addEventListener('click', () => logEvent(el.event));
        });

        return () => {
            followButtons.map((el) => {
                document
                    .querySelector(el.DOMnode)
                    ?.removeEventListener('click', () => logEvent(el.event));
            });
        };
    });

    useEffect(() => {
        const contactButtons = [
            {
                DOMnode: '.descriptive-organizer-info__actions .eds-btn--link',
                event: STATSIG_EVENT_NAMES.LISTING_ORGANIZER_CONTACT_BUTTON,
            },
            {
                DOMnode: '.cc-list-actions .eds-btn--link',
                event: STATSIG_EVENT_NAMES.LISTING_ORGANIZER_CONTACT_BUTTON,
            },
        ];
        contactButtons.map((el) => {
            document
                .querySelector(el.DOMnode)
                ?.addEventListener('click', () => logEvent(el.event));
        });

        return () => {
            contactButtons.map((el) => {
                document
                    .querySelector(el.DOMnode)
                    ?.removeEventListener('click', () => logEvent(el.event));
            });
        };
    });

    return (
        <div data-spec="listing-page-app">
            <SettingsContext.Provider value={props.settings}>
                <UserContext.Provider value={props.user}>
                    <TrackingContext.Provider
                        value={getTrackingContextFrom(props)}
                    >
                        <OrganizerContext.Provider value={props.organizer}>
                            <ShareEventRoot
                                {...props}
                                modalContainer={modalContainer}
                            />
                            <NotificationsContextProvider>
                                <FollowOrganizerController
                                    orgFacebook={organizerFacebook}
                                    orgTwitter={organizerTwitter}
                                    orgWebsite={organizerWebsite}
                                    organizerId={organizerId}
                                    organizerName={organizerName}
                                    organizerProfilePicture={
                                        organizerProfilePicture
                                    }
                                    organizerUrl={organizerUrl}
                                    organizerDescription={organizerDescription}
                                    organizerEventTitle={organizerEventTitle}
                                    organizers={[organizerId]}
                                    organizerData={{
                                        [organizerId]: {
                                            id: organizerId,
                                            name: organizerName,
                                            url: organizerUrl,
                                            profilePicture:
                                                organizerProfilePicture,
                                            followedByYou: false,
                                        },
                                    }}
                                    userId={userId && userId.toString()}
                                    captchaKey={captchaKey}
                                    isAuthenticated={isAuthenticated}
                                    eventName={eventName}
                                    eventId={eventId}
                                    eventUrl={eventUrl}
                                    isRemindedEvent={isRemindedEvent}
                                    isGDPRCountry={isGDPRCountry}
                                    gaSettings={gaSettings}
                                    gaCategory={gaSettings.category}
                                />
                                <NotificationBar />
                                <BookmarkRoot
                                    eventId={eventId}
                                    isBookmarked={isBookmarkedEvent}
                                    queryParams={location.search}
                                />
                            </NotificationsContextProvider>
                            <SeriesDatesDropdownRoot
                                childEvents={childEvents || []}
                                eventId={eventId}
                                isOnlineEvent={isOnlineEvent}
                            />
                            <ReportThisEventRoot {...props} />
                            <CreatorCollectionsRoot {...props} />
                            <EnhancedListingComponents
                                {...props}
                                creatorCollections={creatorCollections}
                            />
                            <div
                                id="share-modal-wrapper"
                                ref={modalContainer}
                            ></div>
                            <div id="listings-root__overlay"></div>
                            {/*TODO: Remove this component since it is not used anymore*/}
                            <EventMapRoot {...props} />
                        </OrganizerContext.Provider>
                    </TrackingContext.Provider>
                </UserContext.Provider>
            </SettingsContext.Provider>
        </div>
    );
};
